/**
 * Import phase catalogue used by the DXF progress pipeline.
 *
 * Each phase carries:
 *   - `weight`   — relative time cost in percent (weights across all phases
 *                  sum to 100). The UI renders a monotonic 0-100 bar by
 *                  accumulating weights as phases complete, and interpolates
 *                  within a phase using `itemsProcessed / itemsTotal`.
 *   - `labelKey` — i18n message key (RU/EN dictionaries carry the label).
 *
 * Phase boundaries mirror the AutoCAD "Processing Objects" dialog that
 * users see when opening large DWG/DXF files. The pipeline:
 *
 *   1. fetch     — network fetch of the .dxf file (5%)
 *   2. decode    — TextDecoder.decode (CP1251/UTF-8) (5%)
 *   3. tokenize  — single-pass tokenizeDxf → DxfGroup[] (25%)
 *   4. sections  — group DxfGroup[] by top-level section (5%)
 *   5. entities  — parse ENTITIES section → DxfSupportedEntity[] (25%)
 *   6. bridge    — build DrawingDocument (importDxfDocumentToDrawingDocument)
 *                  (15%)
 *   7. merge     — mergeImportedGeometryDocument dedup + union (10%)
 *   8. sync      — syncShellStateFromMergedGeometryDocument rebuild of
 *                  EntityPreview[] + shell spaces (10%)
 *
 * The weights were tuned against АБИ1.dxf (11MB, ~5000 entities) measured
 * with performance.now() markers: tokenize and entity parse together are
 * 50% of the wall-clock, and merge/sync together are ~20% because they
 * touch the full entity graph twice.
 *
 * Shared between `packages/drawing-model/src/dxfBridge.ts` (emitter) and
 * the editor UI (`apps/web/src/app/components/ImportProgressDialog.tsx`)
 * so both ends refer to the same canonical phase identifiers and weights.
 */

export const IMPORT_PHASES = {
  fetch: { weight: 5, labelKey: "dxfio.progress.fetch" },
  decode: { weight: 5, labelKey: "dxfio.progress.decode" },
  tokenize: { weight: 25, labelKey: "dxfio.progress.tokenize" },
  sections: { weight: 5, labelKey: "dxfio.progress.sections" },
  entities: { weight: 25, labelKey: "dxfio.progress.entities" },
  bridge: { weight: 15, labelKey: "dxfio.progress.bridge" },
  merge: { weight: 10, labelKey: "dxfio.progress.merge" },
  sync: { weight: 10, labelKey: "dxfio.progress.sync" },
} as const;

export type ImportPhaseName = keyof typeof IMPORT_PHASES;

/**
 * Ordered phase names — the UI walks this list to compute the accumulated
 * weight before the current phase (so percent monotonically grows across
 * phases without going backwards when the next phase starts at 0%).
 */
export const IMPORT_PHASE_ORDER: readonly ImportPhaseName[] = [
  "fetch",
  "decode",
  "tokenize",
  "sections",
  "entities",
  "bridge",
  "merge",
  "sync",
] as const;

/**
 * Signature of the progress callback accepted by importers. The bridge
 * calls it between phases and, for high-cardinality phases (entities,
 * merge, sync), every N items. Passing `itemsProcessed === itemsTotal`
 * marks the end of a phase.
 *
 * Callbacks SHOULD NOT assume they run on the main thread — in future
 * iterations the parser may move to a worker. Inside the callback the
 * UI-side implementation is expected to schedule a state update (zustand
 * set) and yield via `queueMicrotask` / `setTimeout(0)` so React can
 * paint before the next phase starts.
 */
export type ImportProgressCallback = (
  phase: ImportPhaseName,
  percent: number,
  itemsProcessed: number,
  itemsTotal: number,
) => void;

/**
 * Compute the global 0-100 percent for a given phase + intra-phase
 * progress. Used by the UI to render a monotonic bar.
 *
 *   globalPercent = sum(weights before phase)
 *                 + weight[phase] * (itemsProcessed / itemsTotal)
 *
 * Guards against `itemsTotal === 0` (returns the phase start boundary).
 */
export function computeGlobalPercent(
  phase: ImportPhaseName,
  itemsProcessed: number,
  itemsTotal: number,
): number {
  let accumulated = 0;
  for (const name of IMPORT_PHASE_ORDER) {
    if (name === phase) break;
    accumulated += IMPORT_PHASES[name].weight;
  }
  const phaseWeight = IMPORT_PHASES[phase].weight;
  const fraction = itemsTotal > 0 ? Math.min(1, itemsProcessed / itemsTotal) : 0;
  const globalPercent = accumulated + phaseWeight * fraction;
  return Math.max(0, Math.min(100, globalPercent));
}
